import { Box, Divider, Heading, Icon, IconButton, Menu, Paragraph, Tag, TextButton } from '@zillow/constellation-10';
import { IconMessageFilled, IconMoreFilled, IconPhoneFilled } from '@zillow/constellation-icons';
import { useAuth } from '../../../AuthProvider';
import { FULLSTORY_EXCLUDE_CLASS_NAME, getLatestMessageDateText } from '@zg-rentals/satellite-components';
import type { SatelliteWebLandlordLeadManagementTableRowResponseDto } from '@zg-rentals/ts-satellite-client';
import { forwardRef, useContext } from 'react';
import Link from 'next/link';
import { AppContext } from '../../../contexts/AppContextProvider';
import useLeadManagementActions from '../../../hooks/useLeadManagementActions';
import formatListingAddress from '../../../utils/formatListingAddress';
import getPropertyDashboardOverviewUrl from '../../../utils/getPropertyDashboardOverviewUrl';
import getRenterDisplayName, { isNamelessPhoneLead } from '../../../utils/getRenterDisplayName';
import useDevices from '../../../hooks/useDevices';
import { InboxPageMobileView } from '../../../types/InboxPageMobileView';
import useModal from '../../../hooks/useModal';
import { ModalType } from '../../../../src/types/ModalType';
import {
  CLICK_OTHER_ACTIONS_MENU_EVENT_TYPE_ID,
  emitClickOtherActionsMenuEvent,
} from '../../../constants/clickstreamEvents/otherActionsMenu';
import StarIconButton from '../../StarIconButton';
import {
  emitOpenReportProblemModalEvent,
  OPEN_REPORT_PROBLEM_MODAL_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../../../constants/clickstreamEvents/globals';

export type RenterDetailsHeaderProps = {
  lead: SatelliteWebLandlordLeadManagementTableRowResponseDto | null;
  conversationId: string;
};

const RenterDetailsHeader = forwardRef<HTMLDivElement, RenterDetailsHeaderProps>(
  ({ lead, conversationId }, ref): JSX.Element => {
    const { isImpersonator, paths } = useContext(AppContext);
    const { user } = useAuth();
    const { isMdAndBelow } = useDevices();
    const {
      handleArchiveClick,
      handleCallClick,
      handleMarkAsUnread,
      handleMarkAsRead,
      handleOpenRenterMessagingModal,
      handleViewMessageClick,
      handleOptionsInOtherActionsClickstreamEvent,
    } = useLeadManagementActions({
      conversationId,
    });
    const { setActiveModal } = useModal();

    // Listing identity comes from the open lead, not page-level context/URL — so it stays correct
    // when multiple listings are selected (the URL alias is then a comma-joined list).
    const { renterInfo, status, latestContact, listingDetails } = lead || {};
    const listingAlias = listingDetails?.listingAlias ?? '';
    const listingAddress = formatListingAddress(listingDetails?.address);
    const { leadId, renterName, renterPhoneNumber, firstContactDateMs } = renterInfo || {};
    const { isConversationBlockedByZillow, isFavorite } = status || {};
    const { messageDateMs, isNew, isRead, isPhoneLead } = latestContact || {};

    const firstContactTimestamp = firstContactDateMs ? getLatestMessageDateText(String(firstContactDateMs)) : '';
    const renterNameDisplay = getRenterDisplayName(renterName, isPhoneLead, '');
    const isPhoneLeadLabel = isNamelessPhoneLead(renterName, isPhoneLead);

    const hasPhoneNumber = !!renterPhoneNumber;

    const listingDashboardOverviewUrl = getPropertyDashboardOverviewUrl(
      paths.myPropertiesPageWithBasePath,
      listingAlias,
    );

    const handleMenuOpenClickstreamEvent = () => {
      emitClickOtherActionsMenuEvent(user, { listingAlias }, { leadId });
    };

    // The inbox parses the slug positionally as [listingAlias, conversationId], so without the
    // lead's alias the conversation can't be targeted — fall back to the inbox root (empty query).
    const inboxMessageHref = {
      pathname: '/inbox/[[...conversationSlug]]',
      query: listingAlias
        ? {
            conversationSlug: [listingAlias, conversationId],
            mobileView: InboxPageMobileView.MessageList,
          }
        : {},
    };

    const handleMessageButtonClick = () => {
      handleViewMessageClick();
      if (!isMdAndBelow) {
        handleOpenRenterMessagingModal();
      }
    };

    const handleOpenReportConversationModal = () => {
      emitOpenReportProblemModalEvent({
        user,
        listing: { listingAlias },
        triggerLocation: TRIGGER_LOCATION.LeadManagementPage,
        lead: { leadId },
      });
      setActiveModal(ModalType.ReportConversationModal);
      handleOptionsInOtherActionsClickstreamEvent('report');
    };

    const moreActionsMenu = (
      <>
        {isRead ? (
          <Menu.Item onClick={handleMarkAsUnread} disabled={isImpersonator}>
            Mark unread
          </Menu.Item>
        ) : (
          <Menu.Item
            onClick={() => handleMarkAsRead(messageDateMs ? String(messageDateMs) : '')}
            disabled={isImpersonator}
          >
            Mark read
          </Menu.Item>
        )}
        <Divider />
        <Menu.Item onClick={handleArchiveClick} disabled={isImpersonator}>
          Archive
        </Menu.Item>
        <Menu.Item
          data-eventid={OPEN_REPORT_PROBLEM_MODAL_EVENT_TYPE_ID}
          onClick={handleOpenReportConversationModal}
          disabled={isImpersonator || isConversationBlockedByZillow}
        >
          Report
        </Menu.Item>
      </>
    );

    return (
      <>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} ref={ref}>
          <Box css={{ display: 'flex', alignItems: 'center', marginLeft: '-tight', marginTop: 'default' }}>
            <StarIconButton
              conversationId={conversationId}
              isConversationBlockedByZillow={isConversationBlockedByZillow || false}
              isFavorite={isFavorite || false}
              hasMargin={true}
            />
            <Heading
              className={FULLSTORY_EXCLUDE_CLASS_NAME}
              level={4}
              textStyle="heading-sm"
              css={{ textTransform: isPhoneLeadLabel ? 'none' : 'capitalize' }}
            >
              {renterNameDisplay}
            </Heading>
            {isNew && (
              <Tag tone="aqua" size="sm" css={{ marginLeft: 'tighter', textTransform: 'uppercase' }}>
                New
              </Tag>
            )}
          </Box>
          <Box css={{ display: 'flex', marginTop: 'default' }}>
            <IconButton
              emphasis="bare"
              asChild={isMdAndBelow}
              icon={
                <Icon css={{ color: 'icon.muted' }}>
                  <IconMessageFilled />
                </Icon>
              }
              onClick={handleMessageButtonClick}
              title="Message"
              tone="neutral"
              css={{
                marginRight: 'tighter',
              }}
            >
              {isMdAndBelow ? <Link href={inboxMessageHref} /> : undefined}
            </IconButton>
            {hasPhoneNumber && (
              <IconButton
                emphasis="bare"
                icon={
                  <Icon css={{ color: 'icon.muted' }}>
                    <IconPhoneFilled />
                  </Icon>
                }
                onClick={handleCallClick}
                title="Call"
                tone="neutral"
                css={{
                  marginRight: 'tighter',
                }}
              />
            )}

            <Menu
              content={moreActionsMenu}
              trigger={
                <IconButton
                  data-eventid={CLICK_OTHER_ACTIONS_MENU_EVENT_TYPE_ID}
                  emphasis="bare"
                  icon={
                    <Icon css={{ color: 'icon.muted' }}>
                      <IconMoreFilled />
                    </Icon>
                  }
                  title="More actions"
                  tone="neutral"
                />
              }
              onOpenChange={(isOpen) => {
                if (isOpen) {
                  handleMenuOpenClickstreamEvent();
                }
              }}
              css={{ fontFamily: 'sansSerif' }}
            />
          </Box>
        </Box>
        <Box css={{ marginLeft: '41px' }}>
          <TextButton asChild textStyle="body">
            <a href={listingDashboardOverviewUrl}>{listingAddress}</a>
          </TextButton>
          <Paragraph
            data-testid="first-contact-time"
            css={{ color: 'text.subtle', marginBottom: 'loose', marginTop: 'tighter' }}
          >
            First contact: {firstContactTimestamp}
          </Paragraph>
        </Box>
      </>
    );
  },
);

RenterDetailsHeader.displayName = 'RenterDetailsHeader';
export default RenterDetailsHeader;
